import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Loader2, ArrowLeft, Clock, User, Calendar, FileText, MessageSquare, Edit2, Save, FileDown, AlertTriangle, ArrowUp, Wand2 } from 'lucide-react';
import { FeedbackRequest, FeedbackAttachment } from '../types/feedback';
import { useApiClient } from '../hooks/useApiClient';
import { useUserProfileContext } from '../context/UserProfileContext';
import { formatDateInIST, formatDateTimeInIST, getEmployeeSubmittedDate, getManagerCompletedDate, mapApiFeedbackRequestFull } from '../utils/mappers';
import { useToast } from '../hooks/useToast';
import { Toast, ConfirmDialog } from '../components/Toast';
import { CorePrioritiesView } from '../components/CorePrioritiesView';
import { generateConnectPDFContent, printHtmlInNewWindow } from '../utils/pdfTemplates';
import AttachmentList from '../components/AttachmentList';
import FileUpload from '../components/FileUpload';
import { RichTextEditor } from '../components/common/RichTextEditor';
import { isRichTextEmpty, renderRichText } from '../utils/richText';
import { CvUpdatePreviewModal } from '../components/CvUpdatePreviewModal';
import { getCvUpdateStats } from '../utils/userCvApi';
import { CvUpdateStats } from '../types/userCv';
import { useFeatures } from '../hooks/useFeatures';
import { Link2, FileX } from 'lucide-react';

type TabType = 'responses' | 'timeline';

export function ViewFeedbackRequest() {
  const navigate = useNavigate();
  const location = useLocation();
  const startEditing = Boolean(location.state?.startEditing);
  const { id } = useParams<{ id: string }>();
  const { apiCall } = useApiClient();
  const { userProfile } = useUserProfileContext();
  const { userCvUi } = useFeatures();
  const { toasts, hideToast, success, error: showError } = useToast();
  
  const [request, setRequest] = useState<FeedbackRequest | null>(location.state?.request || null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('responses');
  const [isEditing, setIsEditing] = useState(false);
  const [editedAnswers, setEditedAnswers] = useState<Record<number, string>>({});
  const [editedConnectName, setEditedConnectName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [submitConfirmMessage, setSubmitConfirmMessage] = useState('');
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [attachments, setAttachments] = useState<FeedbackAttachment[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{ name?: boolean; questions?: Set<number> }>({});
  const [showCvUpdateModal, setShowCvUpdateModal] = useState(false);
  const [cvStats, setCvStats] = useState<CvUpdateStats | null>(null);

  const hasCvSourceData = !!request && (
    request.answers?.some(answer => answer.answer?.trim() || answer.managerFeedback?.trim()) ||
    !!request.overallComments?.trim()
  );
  const canUpdateCv = hasCvSourceData || attachments.length > 0;

  const loadCvStats = useCallback(async () => {
    if (!userCvUi) return;
    if (!request || request.status !== 'completed' || !userProfile?.employeeId) return;
    try {
      const stats = await getCvUpdateStats(userProfile.employeeId, request.id, apiCall);
      setCvStats(stats);
    } catch {
      // non-fatal: hide badge on error
      setCvStats(null);
    }
  }, [request, userProfile?.employeeId, apiCall, userCvUi]);

  useEffect(() => {
    loadCvStats();
  }, [loadCvStats]);

  // Back-to-top scroll listener
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (id) {
      fetchRequestDetails();
      fetchAttachments();
    }
  }, [id]);

  useEffect(() => {
    if (request && (request.status === 'draft' || request.status === 'revision_requested')) {
      // Initialize edited answers
      const answersMap: Record<number, string> = {};
      request.answers?.forEach(answer => {
        answersMap[answer.questionId] = answer.answer;
      });
      setEditedAnswers(answersMap);
      setEditedConnectName(request.connectName || '');
    }
  }, [request]);

  useEffect(() => {
    if (startEditing && request && (request.status === 'draft' || request.status === 'revision_requested')) {
      setIsEditing(true);
    }
  }, [startEditing, request]);

  const fetchRequestDetails = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const response = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests/${id}`
      );
      if (response.ok) {
        const result = await response.json();
        if (result.data) {
          setRequest(mapApiFeedbackRequestFull(result.data));
        }
      } else {
        showError('Failed to fetch request details');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error fetching request details:', error);
      showError('Error loading feedback request');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttachments = async () => {
    if (!id) return;
    
    setLoadingAttachments(true);
    try {
      const response = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/Attachments/feedback-request/${id}`
      );
      if (response.ok) {
        const result = await response.json();
        if (result.data) {
          setAttachments(result.data);
        } else {
          setAttachments([]);
        }
      } else {
        setAttachments([]);
      }
    } catch (error) {
      console.error('Error fetching attachments:', error);
      setAttachments([]);
    } finally {
      setLoadingAttachments(false);
    }
  };

  const uploadFiles = async (requestId: number, files: File[]) => {
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('uploadedBy', userProfile?.email || 'unknown');

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/Attachments/upload/${requestId}`,
          {
            method: 'POST',
            body: formData
          }
        );

        if (!response.ok) {
          console.error(`Failed to upload file: ${file.name}`);
        }
      }
      // Refresh attachments after upload
      await fetchAttachments();
      setSelectedFiles([]);
    } catch (error) {
      console.error('Error uploading files:', error);
    }
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    try {
      const response = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/Attachments/${attachmentId}`,
        {
          method: 'DELETE'
        }
      );

      if (response.ok) {
        success('Attachment deleted successfully');
        await fetchAttachments();
      } else {
        showError('Failed to delete attachment');
      }
    } catch (error) {
      console.error('Error deleting attachment:', error);
      showError('Error deleting attachment');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'revision_requested':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const formatStatus = (status: string) => {
    return status.replace('_', ' ').toUpperCase();
  };

  const handleAnswerChange = (questionId: number, value: string) => {
    setEditedAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    // Clear validation error for this question when user types
    if (validationErrors.questions?.has(questionId)) {
      setValidationErrors(prev => {
        const newQuestions = new Set(prev.questions);
        newQuestions.delete(questionId);
        return { ...prev, questions: newQuestions.size > 0 ? newQuestions : undefined };
      });
    }
  };

  const validateForm = (): boolean => {
    if (!request) return false;
    const errors: { name?: boolean; questions?: Set<number> } = {};
    let hasErrors = false;

    // Validate at least one question is answered
    const unanswered = new Set<number>();
    const questionList = request.questions?.length ? request.questions : request.answers?.map(a => ({ id: a.questionId })) || [];
    questionList.forEach(q => {
      const answer = editedAnswers[q.id] || '';
      if (isRichTextEmpty(answer)) {
        unanswered.add(q.id);
      }
    });

    const answeredCount = questionList.length - unanswered.size;
    if (answeredCount === 0) {
      errors.questions = unanswered;
      hasErrors = true;
    }

    setValidationErrors(errors);

    if (hasErrors) {
      // Scroll to first unanswered question
      const firstId = questionList.find(q => unanswered.has(q.id))?.id;
      if (firstId) {
        const el = document.getElementById(`question-${firstId}`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      showError('Please provide at least one response before submitting.');
    }

    return !hasErrors;
  };

  const handleSaveDraft = async () => {
    if (!request || !id) return;
    if (request.status !== 'draft') {
      showError('Only draft requests can be saved as draft.');
      return;
    }

    setIsSaving(true);
    try {
      // Update draft
      const updateDraftDto = {
        employeeId: request.employeeId,
        employeeName: request.employeeName,
        employeeEmail: request.employeeEmail,
        managerEmployeeId: request.managerEmployeeId,
        managerName: request.managerName,
        managerEmail: request.managerEmail,
        connectName: editedConnectName || null,
        status: 'draft'
      };

      const response = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests/${id}/draft`,
        {
          method: 'PUT',
          body: JSON.stringify(updateDraftDto)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update draft');
      }

      // Update answers
      const submitAnswersDto = {
        userId: request.employeeId,
        userName: request.employeeName,
        isDraft: true,
        answers: request.questions?.map(q => ({
          questionId: q.id,
          answer: editedAnswers[q.id] || ''
        })) || []
      };

      const answersResponse = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests/${id}/answers`,
        {
          method: 'POST',
          body: JSON.stringify(submitAnswersDto)
        }
      );

      if (!answersResponse.ok) {
        throw new Error('Failed to save answers');
      }

      // Upload files if any are selected
      if (selectedFiles.length > 0 && id) {
        await uploadFiles(parseInt(id), selectedFiles);
      }

      success('Draft saved successfully!');
      setIsEditing(false);
      fetchRequestDetails();
    } catch (error) {
      console.error('Error saving draft:', error);
      showError('Failed to save draft');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitDraft = async () => {
    if (!request || !id) return;

    if (!validateForm()) return;

    const questionList = request.questions?.length ? request.questions : request.answers?.map(a => ({ id: a.questionId })) || [];
    const unansweredCount = questionList.filter(q => isRichTextEmpty(editedAnswers[q.id] || '')).length;

    if (unansweredCount > 0) {
      setSubmitConfirmMessage(
        `${unansweredCount} question${unansweredCount > 1 ? 's are' : ' is'} unanswered. Do you still want to submit?`
      );
    } else {
      setSubmitConfirmMessage(
        isRevisionRequested
          ? 'Are you sure you want to submit your revised self-assessment?'
          : 'Are you sure you want to submit this connect? Once submitted, you cannot edit it unless your manager requests a revision.'
      );
    }

    setShowSubmitConfirm(true);
  };

  const confirmSubmitDraft = async () => {
    setShowSubmitConfirm(false);
    if (!request || !id) return;

    setIsSubmitting(true);
    try {
      const submitAnswersDto = {
        userId: request.employeeId,
        userName: request.employeeName,
        selfRating: request.selfRating,
        answers: request.questions?.length
          ? request.questions.map(q => ({
              questionId: q.id,
              answer: editedAnswers[q.id] || ''
            }))
          : request.answers?.map(a => ({
              questionId: a.questionId,
              answer: editedAnswers[a.questionId] || ''
            })) || []
      };

      if (request.status === 'draft') {
        // Update draft status — use current manager from profile if available (handles manager change)
        const currentManager = userProfile?.manager;
        const updateDto = {
          employeeId: request.employeeId,
          employeeName: request.employeeName,
          employeeEmail: request.employeeEmail,
          managerEmployeeId: currentManager?.employeeId || request.managerEmployeeId,
          managerName: currentManager?.displayName || request.managerName,
          managerEmail: currentManager?.email || request.managerEmail,
          connectName: editedConnectName || null,
          status: 'in_progress'
        };

        const updateResponse = await apiCall(
          `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests/${id}/draft`,
          {
            method: 'PUT',
            body: JSON.stringify(updateDto)
          }
        );

        if (!updateResponse.ok) {
          throw new Error('Failed to submit connect');
        }
      }

      // For both draft and revision_requested, submitting answers is required.
      const answersResponse = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests/${id}/answers`,
        {
          method: 'POST',
          body: JSON.stringify(submitAnswersDto)
        }
      );

      if (!answersResponse.ok) {
        throw new Error('Failed to save answers');
      }

      // Upload files if any are selected
      if (selectedFiles.length > 0 && id) {
        await uploadFiles(parseInt(id), selectedFiles);
      }

      success(request.status === 'revision_requested'
        ? 'Revisions submitted successfully!'
        : 'Connect submitted successfully!');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (error) {
      console.error('Error submitting connect:', error);
      showError('Failed to submit connect');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDiscardDraft = async () => {
    setShowDiscardConfirm(true);
  };

  const confirmDiscardDraft = async () => {
    setShowDiscardConfirm(false);
    if (!id) return;

    try {
      const response = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests/${id}`,
        {
          method: 'DELETE'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to discard draft');
      }

      success('Draft discarded successfully!');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (error) {
      console.error('Error discarding draft:', error);
      showError('Failed to discard draft');
    }
  };

  // Export to PDF - for completed requests
  const handleExportPDF = async () => {
    if (!request || request.status !== 'completed') return;
    
    setIsExporting(true);
    try {
      // Fetch core priorities
      let corePriorities = null;
      try {
        const prioritiesResponse = await apiCall(
          `${process.env.REACT_APP_API_BASE_URL}/api/CorePriorities/employee/${encodeURIComponent(request.employeeId)}/year/${request.year}`
        );
        if (prioritiesResponse.ok) {
          const prioritiesResult = await prioritiesResponse.json();
          if (prioritiesResult.data) {
            corePriorities = prioritiesResult.data;
          }
        }
      } catch (err) {
        console.log('No core priorities found for this connect');
      }

      const pdfContent = generateConnectPDFContent(request, corePriorities);
      printHtmlInNewWindow(pdfContent);
    } catch (err) {
      console.error('Error exporting PDF:', err);
      showError('Failed to generate PDF');
    } finally {
      setIsExporting(false);
    }
  };

  const isDraft = request?.status === 'draft';
  const isRevisionRequested = request?.status === 'revision_requested';
  // AUTHENTICATION BYPASSED - Always allow editing drafts in development
  const canEdit = isDraft || isRevisionRequested; // && request?.employeeId === userProfile?.employeeId;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-pink-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-600 text-lg">Loading connect request...</p>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center text-slate-900">
          <p className="text-xl mb-4">Connect request not found</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 px-6 py-2 rounded-lg text-white shadow-lg shadow-pink-500/25"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }
  const employeeSubmittedDate = getEmployeeSubmittedDate(request.timeline) || (request.status !== 'draft' ? request.requestDate : undefined);
  const managerCompletedDate = getManagerCompletedDate(request.timeline) || (request.status === 'completed' ? request.updatedAt : undefined);

  return (
    <>
      <div className="pt-24 min-h-screen bg-slate-50">
        <main className="mx-auto px-10 lg:px-20 xl:px-32 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-pink-500 hover:text-pink-600 transition-colors mb-4 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>

          <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-2xl p-8 text-white shadow-xl">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-white">
                    {request.connectName || request.employeeName || 'Connect Request'}
                  </h1>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${getStatusColor(request.status)}`}>
                    {formatStatus(request.status)}
                  </span>
                </div>
                
                <div className="flex flex-wrap gap-4 text-sm text-white/70">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {formatDateTimeInIST(request.requestDate)} IST
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Manager: {request.managerName}
                  </div>
                  {request.year && request.quarter && (
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {request.quarter} {request.year}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Export PDF button for completed requests */}
              {request.status === 'completed' && (
                <div className="flex items-center gap-2 flex-wrap">
                  {userCvUi && cvStats && (
                    cvStats.updateCount > 0 ? (
                      <div
                        className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/20 backdrop-blur-sm text-white text-xs font-semibold border border-white/30"
                        title={cvStats.lastUpdatedAt ? `Last updated ${formatDateTimeInIST(cvStats.lastUpdatedAt)} IST` : undefined}
                      >
                        <Link2 className="w-3.5 h-3.5" />
                        <span>
                          CV updated {cvStats.updateCount} time{cvStats.updateCount === 1 ? '' : 's'} from this Connect
                          {cvStats.lastUpdatedAt && <> · last on {formatDateTimeInIST(cvStats.lastUpdatedAt)} IST</>}
                          {cvStats.latestVersionAfterUpdate && <> · v{cvStats.latestVersionAfterUpdate}</>}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 backdrop-blur-sm text-white/85 text-xs font-medium border border-white/20">
                        <FileX className="w-3.5 h-3.5" />
                        <span>CV not updated from this Connect yet</span>
                      </div>
                    )
                  )}
                  {userCvUi && (
                    <button
                      onClick={() => setShowCvUpdateModal(true)}
                      disabled={loadingAttachments || !canUpdateCv}
                      className="flex items-center gap-2 px-5 py-3 bg-white text-pink-600 font-semibold rounded-xl border border-white shadow-md hover:shadow-lg hover:bg-pink-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:shadow-md"
                      title={canUpdateCv
                        ? 'Use AI to update your CV with skills/learnings from this Connect'
                        : 'Add responses or a supporting document before updating your CV'}
                    >
                      <Wand2 className="w-4 h-4" />
                      <span>Update CV</span>
                    </button>
                  )}
                  <button
                    onClick={handleExportPDF}
                    disabled={isExporting}
                    className="flex items-center gap-2 px-5 py-3 bg-white/20 backdrop-blur-sm text-white font-semibold rounded-xl border border-white/30 hover:bg-white/30 hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isExporting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Exporting...</span>
                      </>
                    ) : (
                      <>
                        <FileDown className="w-4 h-4" />
                        <span>Export PDF</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Status Banner - contextual message based on request status */}
        {isDraft && (
          <div className="mb-6 rounded-2xl p-5 border-2 border-slate-300 bg-slate-50 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-slate-200 flex items-center justify-center">
                <Edit2 className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-800">Draft — Not Yet Submitted</h3>
                <p className="text-slate-600 text-sm">This connect is still a draft. Complete your self-assessment and click Submit when you're ready.</p>
              </div>
            </div>
          </div>
        )}
        {request.status === 'in_progress' && (
          <div className="mb-6 rounded-2xl p-5 border-2 border-blue-300 bg-blue-50 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-blue-800">Awaiting Manager Review</h3>
                <p className="text-blue-700 text-sm">Your connect has been submitted. Your manager will review your self-assessment and provide feedback.</p>
              </div>
            </div>
          </div>
        )}
        {isRevisionRequested && (
          <div className="mb-6 rounded-2xl p-5 border-2 border-amber-400 bg-amber-50 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-amber-800">Revision Requested</h3>
                <p className="text-amber-700 text-sm">Your manager has requested changes to your self-assessment. Please review their comments, update your responses, and resubmit.</p>
              </div>
            </div>
          </div>
        )}
        {request.status === 'completed' && !request.pip?.isOnPip && (
          <div className="mb-6 rounded-2xl p-5 border-2 border-green-300 bg-green-50 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-green-800">Review Complete</h3>
                <p className="text-green-700 text-sm">Your manager has completed the review. You can view their feedback below or export this connect as a PDF.</p>
              </div>
            </div>
          </div>
        )}

        {/* PIP Banner - shown to employee when on PIP */}
        {request.pip && request.pip.isOnPip && (
          <div className="mb-6 rounded-2xl p-6 border-2 border-red-400 bg-red-50 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-red-800 mb-1">
                  Performance Improvement Plan (PIP)
                </h3>
                <div className="flex flex-wrap gap-4 text-sm">
                  {request.pip.pipStartDate && (
                    <div className="flex items-center gap-2 text-red-700 bg-red-100 px-3 py-1.5 rounded-lg">
                      <Calendar className="w-4 h-4" />
                      <span className="font-semibold">Start:</span>
                      {formatDateTimeInIST(request.pip.pipStartDate)} IST
                    </div>
                  )}
                  {request.pip.pipEndDate && (
                    <div className="flex items-center gap-2 text-red-700 bg-red-100 px-3 py-1.5 rounded-lg">
                      <Calendar className="w-4 h-4" />
                      <span className="font-semibold">End:</span>
                      {formatDateTimeInIST(request.pip.pipEndDate)} IST
                    </div>
                  )}
                </div>
                {request.pip.pipReason && (
                  <div className="mt-3 p-3 bg-red-100 rounded-lg border border-red-300">
                    <p className="text-red-800 text-sm">
                      <span className="font-semibold">Reason:</span> {request.pip.pipReason}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Core Priorities - View Only */}
        <CorePrioritiesView
          employeeId={request.employeeId}
          employeeName={request.employeeName}
          year={request.year || new Date().getFullYear()}
        />

        {/* Tabs */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-6">
          <div className="flex gap-2 p-2">
            <button
              onClick={() => setActiveTab('responses')}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                activeTab === 'responses'
                  ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg shadow-pink-500/25'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
              }`}
            >
              <FileText className="w-5 h-5" />
              Connect Responses
            </button>
            <button
              onClick={() => setActiveTab('timeline')}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                activeTab === 'timeline'
                  ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg shadow-pink-500/25'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
              }`}
            >
              <Clock className="w-5 h-5" />
              Activity Timeline
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'responses' && (
          <>
            {/* Connect Name Edit (for drafts) */}
            {isDraft && canEdit && isEditing && (
              <div className="bg-white rounded-2xl p-6 mb-6 border border-slate-200 shadow-sm">
                <label className="block text-slate-800 font-semibold mb-2">
                  Connect Name (Optional)
                </label>
                <input
                  type="text"
                  value={editedConnectName}
                  onChange={(e) => setEditedConnectName(e.target.value)}
                  placeholder="Enter a custom name for this connect..."
                  className="w-full px-4 py-3 bg-slate-50 text-slate-900 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-300 transition-all"
                />
              </div>
            )}

            {/* Questions and Answers */}
            <div className="space-y-5 mb-8">
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-gradient-to-r from-pink-200 to-transparent"></div>
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                  <FileText className="w-4 h-4 text-pink-400" />
                  Connect Responses
                </h2>
                <div className="h-px flex-1 bg-gradient-to-l from-pink-200 to-transparent"></div>
              </div>
              {(!request.questions || request.questions.length === 0) && (!request.answers || request.answers.length === 0) ? (
                <div className="bg-white rounded-2xl p-12 border border-slate-200 shadow-sm text-center">
                  <p className="text-slate-500 text-lg">No questions available.</p>
                </div>
              ) : request.questions && request.questions.length > 0 ? (
                request.questions.map((question, index) => {
                  const answer = request.answers?.find(a => a.questionId === question.id);
                  return (
                    <div
                      key={question.id}
                      id={`question-${question.id}`}
                      className={`rounded-2xl overflow-hidden shadow-sm transition-all ${
                        validationErrors.questions?.has(question.id)
                          ? 'ring-1 ring-red-300 bg-white'
                          : 'bg-white border border-slate-200/80'
                      }`}
                    >
                      {/* Question header */}
                      <div className={`px-5 py-3.5 flex items-center gap-3 border-b ${
                        validationErrors.questions?.has(question.id)
                          ? 'bg-red-50/60 border-red-100'
                          : 'bg-gradient-to-r from-slate-50 to-white border-slate-100'
                      }`}>
                        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                          {index + 1}
                        </span>
                        <h2 className="text-sm font-semibold text-slate-800 leading-relaxed">
                          {question.questionText || question.question}
                        </h2>
                      </div>

                      {/* Response area */}
                      <div className="p-5 space-y-4">
                        {/* Employee's Response */}
                        <div>
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <label className={`text-[11px] font-semibold uppercase tracking-wider ${
                              validationErrors.questions?.has(question.id) ? 'text-red-500' : 'text-slate-400'
                            }`}>
                              Your Response {validationErrors.questions?.has(question.id) && <span className="normal-case text-red-400">— Required</span>}
                            </label>
                            {employeeSubmittedDate && !isEditing && (
                              <span className="text-[11px] text-slate-400 whitespace-nowrap">
                                {formatDateInIST(employeeSubmittedDate)}
                              </span>
                            )}
                          </div>
                          {canEdit && isEditing ? (
                            <div className={`rounded-xl overflow-hidden border ${
                              validationErrors.questions?.has(question.id) ? 'border-red-200 bg-red-50/30' : 'border-slate-200 bg-slate-50/50'
                            }`}>
                              <RichTextEditor
                                value={editedAnswers[question.id] || ''}
                                onChange={(updatedValue) => handleAnswerChange(question.id, updatedValue)}
                                placeholder="Enter your response..."
                                minHeightClassName="min-h-[150px]"
                              />
                            </div>
                          ) : (
                            <div className="bg-slate-50/70 rounded-xl px-4 py-3 text-slate-700 min-h-[80px] border border-slate-100">
                              <div
                                className="rich-text-editor"
                                dangerouslySetInnerHTML={renderRichText(answer?.answer || editedAnswers[question.id] || 'No response provided')}
                              />
                            </div>
                          )}
                        </div>

                        {/* Manager's Feedback */}
                        {!isDraft && !(isRevisionRequested && isEditing) && (
                          <div className={`rounded-xl px-4 py-3 ${
                            answer?.managerFeedback && answer.managerFeedback.trim() !== ''
                              ? 'bg-blue-50/60 border border-blue-100'
                              : 'bg-slate-50/40 border border-slate-100'
                          }`}>
                            <div className="flex items-center justify-between gap-3 mb-2">
                              <div className="flex items-center gap-2">
                                <label className={`text-[11px] font-semibold uppercase tracking-wider ${
                                  answer?.managerFeedback && answer.managerFeedback.trim() !== '' ? 'text-blue-500' : 'text-slate-400'
                                }`}>
                                  Manager Comments
                                </label>
                                {answer?.managerFeedback && answer.managerFeedback.trim() !== '' && (
                                  <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 text-[10px] font-bold rounded-md uppercase">
                                    Reviewed
                                  </span>
                                )}
                              </div>
                              {managerCompletedDate && (
                                <span className="text-[11px] text-blue-400 whitespace-nowrap">
                                  {formatDateInIST(managerCompletedDate)}
                                </span>
                              )}
                            </div>
                            {answer?.managerFeedback && answer.managerFeedback.trim() !== '' ? (
                              <div className="text-slate-700">
                                <div
                                  className="rich-text-editor"
                                  dangerouslySetInnerHTML={renderRichText(answer.managerFeedback)}
                                />
                              </div>
                            ) : (
                              <p className="text-slate-400 text-sm italic">Awaiting feedback from your manager</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                request.answers.map((answer, index) => (
                  <div
                    key={answer.questionId}
                    className="rounded-2xl overflow-hidden shadow-sm bg-white border border-slate-200/80 transition-all"
                  >
                    {/* Question header */}
                    <div className="px-5 py-3.5 flex items-center gap-3 border-b bg-gradient-to-r from-slate-50 to-white border-slate-100">
                      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                        {index + 1}
                      </span>
                      <h2 className="text-sm font-semibold text-slate-800 leading-relaxed">
                        {answer.question}
                      </h2>
                    </div>

                    {/* Response area */}
                    <div className="p-5 space-y-4">
                      {/* Employee's Response */}
                      <div>
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                            Your Response
                          </label>
                          {employeeSubmittedDate && (
                            <span className="text-[11px] text-slate-400 whitespace-nowrap">
                              {formatDateInIST(employeeSubmittedDate)}
                            </span>
                          )}
                        </div>
                        <div className="bg-slate-50/70 rounded-xl px-4 py-3 text-slate-700 min-h-[80px] border border-slate-100">
                          <div
                            className="rich-text-editor"
                            dangerouslySetInnerHTML={renderRichText(answer.answer || 'No response provided')}
                          />
                        </div>
                      </div>

                      {/* Manager's Feedback */}
                      {!isDraft && !(isRevisionRequested && isEditing) && (
                        <div className={`rounded-xl px-4 py-3 ${
                          answer.managerFeedback && answer.managerFeedback.trim() !== ''
                            ? 'bg-blue-50/60 border border-blue-100'
                            : 'bg-slate-50/40 border border-slate-100'
                        }`}>
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <div className="flex items-center gap-2">
                              <label className={`text-[11px] font-semibold uppercase tracking-wider ${
                                answer.managerFeedback && answer.managerFeedback.trim() !== '' ? 'text-blue-500' : 'text-slate-400'
                              }`}>
                                Manager Comments
                              </label>
                              {answer.managerFeedback && answer.managerFeedback.trim() !== '' && (
                                <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 text-[10px] font-bold rounded-md uppercase">
                                  Reviewed
                                </span>
                              )}
                            </div>
                            {managerCompletedDate && (
                              <span className="text-[11px] text-blue-400 whitespace-nowrap">
                                {formatDateInIST(managerCompletedDate)}
                              </span>
                            )}
                          </div>
                          {answer.managerFeedback && answer.managerFeedback.trim() !== '' ? (
                            <div className="text-slate-700">
                              <div
                                className="rich-text-editor"
                                dangerouslySetInnerHTML={renderRichText(answer.managerFeedback)}
                              />
                            </div>
                          ) : (
                            <p className="text-slate-400 text-sm italic">Awaiting feedback from your manager</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Attachments Section */}
            <div className="mt-6 bg-gradient-to-br from-white to-blue-50/30 rounded-2xl p-5 shadow-sm border border-blue-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2.5 bg-blue-100 rounded-lg">
                  <FileText className="w-4 h-4 text-blue-500" />
                </div>
                <div>
                  <h2 className="text-base font-medium text-slate-800">
                    Supporting Documents
                  </h2>
                  <p className="text-slate-400 text-xs">Upload documents to support your connect request</p>
                </div>
              </div>
              
              {/* Show info message when can edit but not currently editing */}
              {canEdit && !isEditing && (isDraft || isRevisionRequested) && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-blue-700 text-sm">
                    💡 Click the "Edit" button above to upload supporting documents
                  </p>
                </div>
              )}
              
              {/* Show file upload when editing drafts or revisions */}
              {canEdit && isEditing && (isDraft || isRevisionRequested) && (
                <>
                  <FileUpload
                    onFilesChange={setSelectedFiles}
                    maxFileSize={10}
                    maxTotalSize={50}
                    disabled={isSaving || isSubmitting}
                  />
                  {selectedFiles.length > 0 && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-300 rounded-lg">
                      <p className="text-green-700 text-sm">
                        ✓ {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} ready to upload. Click "Save Draft" or "Submit" below to attach them.
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* Display existing attachments */}
              {loadingAttachments ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 text-pink-400 animate-spin" />
                </div>
              ) : attachments.length > 0 ? (
                <div className="mt-4">
                  <AttachmentList
                    attachments={attachments}
                    feedbackRequestId={parseInt(id || '0')}
                    onDelete={handleDeleteAttachment}
                    canDelete={canEdit && isEditing && (isDraft || isRevisionRequested)}
                  />
                </div>
              ) : !canEdit || !isEditing ? (
                <div className="text-center py-6 text-slate-400 text-sm">
                  No supporting documents have been uploaded.
                </div>
              ) : null}
            </div>

            {/* Manager's Overall Comments - hide while editing a revision request */}
            {!isDraft && request.status !== 'in_progress' && !(isRevisionRequested && isEditing) && (
              <div className="mt-6 bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
                <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-purple-500" />
                  Manager's Overall Comments
                </h3>
                <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200 min-h-[120px]">
                  <div className="w-full bg-transparent text-slate-700 min-h-[96px]">
                    {request.overallComments && request.overallComments.trim()
                      ? <div className="rich-text-editor" dangerouslySetInnerHTML={renderRichText(request.overallComments)} />
                      : <span className="text-slate-400 italic">No overall comments provided by your manager.</span>}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'timeline' && (
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-500" />
              Activity Timeline
            </h3>
            {request.timeline && request.timeline.length > 0 ? (
              <div className="space-y-3">
                {request.timeline.map((entry, idx) => (
                  <div key={idx} className="flex items-start gap-3 text-sm">
                    <div className="flex-shrink-0 w-2 h-2 rounded-full bg-pink-400 mt-2" />
                    <div className="flex-1">
                      <p className="text-slate-800 font-medium">{entry.action}</p>
                      <p className="text-slate-400 text-xs">
                        {formatDateTimeInIST(entry.date)} IST
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-8">No activity recorded yet.</p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="mt-8 bg-gradient-to-br from-white to-slate-50 rounded-2xl p-5 shadow-sm border border-slate-200 mb-8">
          {canEdit ? (
            <div className="space-y-4">
              {isEditing ? (
                isDraft ? (
                  <div className="flex items-center justify-end gap-3">
                    <button
                      onClick={handleDiscardDraft}
                      className="bg-white text-slate-600 py-2.5 px-5 rounded-xl font-medium text-sm hover:bg-slate-50 transition-all duration-200 border border-slate-300"
                    >
                      Discard Draft
                    </button>
                    <button
                      onClick={handleSaveDraft}
                      disabled={isSaving}
                      className="bg-white text-pink-600 py-2.5 px-5 rounded-xl font-medium text-sm hover:bg-pink-50 transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed border border-pink-200"
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-5 h-5" />
                          Save Draft
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleSubmitDraft}
                      disabled={isSubmitting}
                      className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white py-2.5 px-6 rounded-xl font-medium text-sm shadow-sm shadow-pink-500/20 hover:shadow-md hover:shadow-pink-500/25 transition-all duration-200 flex items-center gap-2 disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        'Submit'
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-end gap-3">
                    <button
                      onClick={() => setIsEditing(false)}
                      className="bg-white text-slate-600 py-2.5 px-5 rounded-xl font-medium text-sm hover:bg-slate-50 transition-all duration-200 border border-slate-300"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmitDraft}
                      disabled={isSubmitting}
                      className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white py-2.5 px-6 rounded-xl font-medium text-sm shadow-sm shadow-pink-500/20 hover:shadow-md hover:shadow-pink-500/25 transition-all duration-200 flex items-center gap-2 disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        'Submit Revisions'
                      )}
                    </button>
                  </div>
                )
              ) : (
                <div className="flex items-center justify-end gap-3">
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="bg-white text-slate-600 py-2.5 px-5 rounded-xl font-medium text-sm hover:bg-slate-50 transition-all duration-200 flex items-center gap-2 border border-slate-300"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Dashboard
                  </button>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white py-2.5 px-6 rounded-xl font-medium text-sm shadow-sm shadow-pink-500/20 hover:shadow-md hover:shadow-pink-500/25 transition-all duration-200 flex items-center gap-2"
                  >
                    <Edit2 className="w-5 h-5" />
                    {isDraft ? 'Edit Draft' : 'Edit Response'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white py-2.5 px-6 rounded-xl font-medium text-sm shadow-sm shadow-pink-500/20 hover:shadow-md hover:shadow-pink-500/25 transition-all duration-200 flex items-center gap-2 mx-auto"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Dashboard
            </button>
          )}
        </div>
        </main>
      </div>

      {/* Back to Top FAB */}
      <button
        onClick={scrollToTop}
        className={`
          fixed bottom-8 right-8 z-50 w-12 h-12 rounded-full
          bg-gradient-to-r from-pink-500 to-purple-600 text-white
          shadow-lg shadow-pink-500/30 hover:shadow-xl hover:shadow-pink-500/40
          flex items-center justify-center
          transition-all duration-300 hover:scale-110
          ${showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}
        `}
        aria-label="Back to top"
      >
        <ArrowUp className="w-5 h-5" />
      </button>

      {/* Toasts */}
    {toasts.map(toast => (
      <Toast
        key={toast.id}
        message={toast.message}
        type={toast.type}
        onClose={() => hideToast(toast.id)}
      />
    ))}

    {/* CV Update Modal */}
    {userCvUi && showCvUpdateModal && request && userProfile && (
      <CvUpdatePreviewModal
        employeeId={userProfile.employeeId}
        connectId={request.id}
        onClose={() => setShowCvUpdateModal(false)}
        onSuccess={(newVersion) => {
          setShowCvUpdateModal(false);
          success(`CV updated as version v${newVersion}. Open Profile to download.`);
          loadCvStats();
        }}
      />
    )}

    {/* Submit Confirmation Dialog */}
    {showSubmitConfirm && (
      <ConfirmDialog
        title={isRevisionRequested ? 'Submit Revisions' : 'Submit Connect Request'}
        message={submitConfirmMessage}
        confirmText={isRevisionRequested ? 'Submit Revisions' : 'Submit Connect'}
        cancelText="Cancel"
        type="info"
        onConfirm={confirmSubmitDraft}
        onCancel={() => setShowSubmitConfirm(false)}
      />
    )}

    {/* Discard Confirmation Dialog */}
    {isDraft && showDiscardConfirm && (
      <ConfirmDialog
        title="Discard Draft"
        message="Are you sure you want to discard this draft? This action cannot be undone and all your progress will be lost."
        confirmText="Discard Draft"
        cancelText="Cancel"
        type="danger"
        onConfirm={confirmDiscardDraft}
        onCancel={() => setShowDiscardConfirm(false)}
      />
    )}
    </>
  );
}





